import { RotateCcw } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import { PERIOD_IDS } from "../lib/filters";
import type { AccountSet, Period } from "../lib/types";
import { Select } from "./Select";
import { FirmMark, Segmented } from "./ui";

/** Barre de filtres unique, identique sur toutes les pages Lite. */
export function FilterBar({ showPeriod = true }: { showPeriod?: boolean }) {
  const t = useTranslation().lite;
  const { filters, setFilters, resetFilters, accounts, firms, strategies, accountSet } = useLiteData();
  const visibleAccounts = accounts.filter(a => !filters.firm || a.firmKey === filters.firm);
  const hasScope = filters.accountId || filters.accountSet !== null || filters.firm || filters.strategyId;
  // Un seul menu « Compte » : un groupe (préfixe `set:`) ou un compte précis, jamais les deux.
  // Sans choix de l'utilisateur, le menu affiche le défaut de la page, sans le surligner.
  const accountValue = filters.accountId ?? (accountSet !== "all" ? `set:${accountSet}` : null);
  const accountIsDefault = !filters.accountId && filters.accountSet === null;
  const onAccount = (v: string | null) => {
    if (v?.startsWith("set:")) setFilters({ accountSet: v.slice(4) as AccountSet, accountId: null });
    // « Tous les comptes » est un choix explicite ; un compte précis ignore le groupe.
    else setFilters({ accountId: v, accountSet: "all" });
  };
  const hasPersonal = accounts.some(a => !a.firm);
  const periodLabel: Record<Period, string> = {
    today: t.periods.todayShort, week: t.periods.weekShort, month: t.periods.monthShort,
    "3m": t.periods.threeMonthsShort, "6m": t.periods.sixMonthsShort, all: t.periods.allShort,
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showPeriod && (
        <Segmented<Period> value={filters.period} options={PERIOD_IDS.map(id => ({ id, label: periodLabel[id] }))} onChange={period => setFilters({ period })} />
      )}
      <Select<string>
        label={t.filters.propFirm}
        allLabel={t.filters.allPropFirms}
        value={filters.firm}
        options={[
          ...firms.map(f => ({ value: f.key, label: f.name, icon: <FirmMark firm={f} size="sm" /> })),
          ...(hasPersonal ? [{ value: "personal", label: t.filters.personal, icon: <FirmMark firm={null} size="sm" /> }] : []),
        ]}
        onChange={firm => setFilters({ firm })}
      />
      <Select<string>
        label={t.filters.account}
        allLabel={t.filters.allAccounts}
        value={accountValue}
        quiet={accountIsDefault}
        wide
        presetsBefore={1}
        presets={[
          { value: "set:active", label: t.filters.activeAccounts },
          { value: "set:challenge", label: t.filters.challengesOnly },
          { value: "set:funded", label: t.filters.fundedOnly },
        ]}
        options={[...visibleAccounts]
          .sort((a, b) => Number(!a.active) - Number(!b.active))
          .map(a => ({ value: a.id, label: a.name, group: a.active ? t.filters.active : t.filters.inactive, icon: <FirmMark firm={a.firm} size="sm" /> }))}
        onChange={onAccount}
      />
      {strategies.length > 0 && (
        <Select<string>
          label={t.filters.strategy}
          allLabel={t.filters.allStrategies}
          value={filters.strategyId}
          options={strategies.map(s => ({ value: s.id, label: s.name, icon: <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }} /> }))}
          onChange={strategyId => setFilters({ strategyId })}
        />
      )}
      {hasScope && (
        <button type="button" onClick={resetFilters} className="h-9 px-2.5 inline-flex items-center gap-2 rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted" title={t.filters.reset}>
          <RotateCcw size={14} />
          <span className="hidden sm:inline">{t.filters.reset}</span>
        </button>
      )}
    </div>
  );
}
